import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { DEFAULT_BRAND, isBrand, type Brand } from "@/brands";

interface BrandContextType {
    brand: Brand;
    setBrand: (brand: Brand) => void;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const useBrand = (): BrandContextType => {
    const context = useContext(BrandContext);

    if (context === undefined) {
        throw new Error("useBrand must be used within a BrandProvider");
    }

    return context;
};

interface BrandProviderProps {
    children: ReactNode;
    /**
     * Brand to use if none is persisted.
     * @default "orion"
     */
    defaultBrand?: Brand;
    /**
     * localStorage key used to persist the user's selection.
     * @default "ui-brand"
     */
    storageKey?: string;
}

export const BrandProvider = ({ children, defaultBrand = DEFAULT_BRAND, storageKey = "ui-brand" }: BrandProviderProps) => {
    const [brand, setBrand] = useState<Brand>(() => {
        if (typeof window !== "undefined") {
            const saved = window.localStorage.getItem(storageKey);
            if (isBrand(saved)) return saved;
        }
        return defaultBrand;
    });

    useEffect(() => {
        window.document.documentElement.setAttribute("data-brand", brand);
        window.localStorage.setItem(storageKey, brand);
    }, [brand, storageKey]);

    return <BrandContext.Provider value={{ brand, setBrand }}>{children}</BrandContext.Provider>;
};
